import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useSecureChat } from "./SecureChatContext";
import { getUserByName } from "../utilities/ApiCalls";
import ProfileImage from "../../Componets/ProfileImage";
const api = require("../../api/secureMessageApi");
const Constant = require("../Constant");

const NewChatModal = ({ onClose, onChatCreated }) => {
  const { currentUser } = useSecureChat();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // Proxy URL function for profile pictures
  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")) return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  // Search users with debounce
  const searchUsers = useCallback(
    async (query) => {
      if (!query.trim() || query.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        setError(null);

        const resp = await getUserByName(encodeURIComponent(query));
        const users = resp?.data?.data || resp?.data || [];

        // Filter out current user
        const filteredUsers = users.filter(
          (user) => user._id !== currentUser._id
        );
        setSearchResults(filteredUsers);
      } catch (error) {
        console.error("Error searching users:", error);
        setError("Failed to search users");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [currentUser]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchUsers]);

  // Create 1:1 chat - accepts user object directly
  const createChat = useCallback(
    async (userId, user) => {
      try {
        setIsCreating(true);
        setError(null);

        const response = await api.createChat(userId);
        const chatData = response?.data || response;
        console.log("[NewChatModal] createChat response:", chatData);

        if (chatData?._id) {
          // Populate the chat with user data
          const populatedChat = {
            ...chatData,
            otherUsers: [
              {
                _id: userId,
                name: user?.name,
                profilePic: user?.profilePic,
              },
            ],
            displayName: user?.name || "Unknown User",
            displayPic: user?.profilePic || "",
            unreadCount:
              typeof chatData?.unreadCount === "number"
                ? chatData.unreadCount
                : 0,
          };

          onChatCreated(populatedChat);
          onClose();
        } else {
          throw new Error("No chat ID returned");
        }
      } catch (error) {
        console.error("Error creating chat:", error);
        setError("Failed to create chat");
      } finally {
        setIsCreating(false);
      }
    },
    [onChatCreated, onClose]
  );

  // Handle user click - directly create chat
  const handleUserClick = async (user) => {
    if (isCreating) return; // Prevent double-clicks
    await createChat(user._id, user);
  };

  // Handle modal close
  const handleClose = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return createPortal(
    <Overlay onClick={handleClose}>
      <ModalContainer>
        <ModalHeader>
          <Title>New Chat</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalContent>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search users by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </SearchContainer>

          {error && (
            <ErrorContainer>
              <ErrorText>{error}</ErrorText>
            </ErrorContainer>
          )}

          {isSearching && (
            <LoadingContainer>
              <LoadingText>Searching...</LoadingText>
            </LoadingContainer>
          )}

          {!isSearching &&
            searchQuery.length >= 2 &&
            searchResults.length === 0 && (
              <EmptyContainer>
                <EmptyText>No users found</EmptyText>
              </EmptyContainer>
            )}

          {searchResults.length > 0 && (
            <ResultsContainer>
              {searchResults.map((user) => (
                <UserItem
                  key={user._id}
                  onClick={() => handleUserClick(user)}
                  $isDisabled={isCreating}
                >
                  <UserAvatar user={user} proxiedUrl={proxiedUrl} />
                  <UserName>{user.name}</UserName>
                </UserItem>
              ))}
            </ResultsContainer>
          )}

          {isCreating && (
            <LoadingContainer>
              <LoadingText>Creating chat...</LoadingText>
            </LoadingContainer>
          )}
        </ModalContent>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

// Sub-component to handle avatar with error fallback
const UserAvatar = ({ user, proxiedUrl }) => {
  return (
    <AvatarContainer>
      <ProfileImage
        src={user.profilePic}
        name={user.name}
        size={40}
        alt={user.name}
        proxiedUrl={proxiedUrl}
        avatarProps={{
          border: "2px solid var(--color-surface)",
        }}
        imgStyle={{
          border: "2px solid var(--color-surface)",
        }}
      />
    </AvatarContainer>
  );
};

export default NewChatModal;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background-color: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-border);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  font-family: "Merriweather", serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-background);
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  background-color: var(--color-background);
  color: var(--color-text);
  transition: all 0.2s ease;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const ErrorContainer = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid var(--color-error);
  border-radius: 4px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--color-error);
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoadingText = styled.p`
  margin: 0;
  color: var(--color-text);
  font-size: 16px;
`;

const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const EmptyText = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
`;

const ResultsContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  margin-bottom: 20px;
  background-color: var(--color-surface);
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: ${(props) => props.$isDisabled ? "not-allowed" : "pointer"};
  transition: all 0.2s ease;
  background-color: transparent;
  border-bottom: 1px solid var(--color-border);
  opacity: ${(props) => props.$isDisabled ? 0.5 : 1};
  pointer-events: ${(props) => props.$isDisabled ? "none" : "auto"};

  &:hover {
    background-color: var(--color-background);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const AvatarContainer = styled.div`
  width: 40px;
  height: 40px;
  margin-right: 12px;
  flex-shrink: 0;
`;

// Avatar and DefaultAvatar are now handled by ProfileImage component

const UserName = styled.span`
  font-size: 16px;
  color: var(--color-text);
  font-weight: 500;
  font-family: "Merriweather", serif;
`;

const SelectedUserContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: var(--color-background);
  border-radius: 6px;
  border: 1px solid var(--color-border);
`;

const SelectedUserText = styled.p`
  margin: 0;
  color: var(--color-text);
  font-size: 14px;
  
  strong {
    font-weight: 600;
    font-family: "Merriweather", serif;
  }
`;

const CreateButton = styled.button`
  background-color: var(--color-text);
  color: var(--color-surface);
  border: 1px solid var(--color-text);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }

  &:disabled {
    background-color: var(--color-border);
    border-color: var(--color-border);
    cursor: not-allowed;
  }
`;
